import { log } from "../core/logger";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import { API_BASE_URL } from "./helpers";
import type { DeactivateResult, IOrganisationAPI, IsOwnerResponse } from "./organisationApi";
import type { Organisation } from "@/types/Organisation";
import type { PaginatedResponse } from "../types/Pagination";

class RemoteOrganisationAPI implements IOrganisationAPI {
  clearCache(): void {
    // No caching implemented in remote API
    return;
  }

  async fetchOrganisationById(id: string): Promise<Organisation | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`API error: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      log("remoteOrganisationApi", "Failed to fetch organisation", error);
      throw error;
    }
  }

  async fetchSerializedOrganisations(): Promise<PaginatedResponse<Organisation>> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }
      const result: PaginatedResponse<Organisation> = await response.json();
      return result;
    } catch (error) {
      log("remoteOrganisationApi", "Failed to fetch organisations", error);
      throw error;
    }
  }

  async checkIsOwnerAndGetMembers(organisationId: string): Promise<IsOwnerResponse | null> {
    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/is-owner`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 403 || response.status === 404) {
          return null;
        }
        throw new Error(`API error: ${response.statusText}`);
      }

      const result: IsOwnerResponse = await response.json();
      return result;
    } catch (error) {
      log("remoteOrganisationApi", "Failed to check owner status", error);
      return null;
    }
  }

  async transferOwner(organisationId: string, newOwnerId: string): Promise<Organisation> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/transfer-owner`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ newOwnerId }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || "Failed to transfer ownership");
    }
    const json = await response.json();
    return json.data as Organisation;
  }

  async setOrganisationLocations(organisationId: string, permissableLocationIds: string[]): Promise<Organisation> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/locations`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ permissableLocationIds }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || "Failed to set organisation locations");
    }
    const json = await response.json();
    return json.organisation as Organisation;
  }

  async getOrganisationLocationBookingStatus(organisationId: string): Promise<{ lockedLocationIds: string[] }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/locations/booking-status`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || "Failed to load organisation location status");
    }
    const json = await response.json();
    return { lockedLocationIds: (json.lockedLocationIds ?? []) as string[] };
  }

  async deactivateOrganisation(id: string): Promise<DeactivateResult> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${id}/deactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || "Failed to deactivate organisation");
    }
    const json = await response.json();
    return json.data as DeactivateResult;
  }

  async reactivateOrganisation(id: string): Promise<{ membersEvaluated: number }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${id}/reactivate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || "Failed to reactivate organisation");
    }
    const json = await response.json();
    return json.data as { membersEvaluated: number };
  }

  async assignMember(organisationId: string, memberId: string): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/members/${memberId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || "Failed to assign member to organisation");
    }
    return { success: true };
  }

  async renameOrganisation(organisationId: string, name: string): Promise<{ success: boolean }> {
    const response = await fetchWithAuth(`${API_BASE_URL}/api/organisations/${organisationId}/name`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ name }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.details || err.error || "Failed to rename organisation");
    }
    return { success: true };
  }
}

export default RemoteOrganisationAPI;
